import React, {
  createContext,
  FC,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppName } from '../../../apps/definition';
import { AppState } from '../../../constants';
import { SupportedLlm } from '../../../models/User';
import { useAppConnectionStatus } from '../../../pages/onboarding/WorkspaceSetupPage/hooks/app-connection-summary';
import { useUserSafe } from '../../../scripts/hooks';

interface ConnectedApps {
  displayName: string;
  appName: AppName;
  id?: number;
  workspaceName?: string;
  connectionName?: string;
}

interface QATextInputBoxStates {
  searchQuery: string;
  mentions: string | undefined;
  searchFilters: ConnectedApps[];
  dynamicPlaceholder: string;
  isBaseLLM: boolean;
  selectedLlm: SupportedLlm | null;
  sortedInstantConnectedApps: ConnectedApps[];
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setMentions: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSearchFilters: React.Dispatch<React.SetStateAction<ConnectedApps[]>>;
  setDynamicPlaceholder: React.Dispatch<React.SetStateAction<string>>;
  setIsBaseLLM: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLlm: React.Dispatch<React.SetStateAction<SupportedLlm | null>>;
  previousSortedAppsRef: React.MutableRefObject<ConnectedApps[]>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  isLoaded: React.MutableRefObject<boolean>;
}

const context = createContext<QATextInputBoxStates | undefined>(undefined);

export const useQATextInputBoxStates = (): QATextInputBoxStates => {
  const ctx = useContext(context);
  if (!ctx) {
    throw new Error('Attempted to use context outside of scope');
  }

  return ctx;
};

export const QATextInputBoxStatesProvider: FC = ({ children }) => {
  const {
    preferredLlm: userPreferredLlm,
    orgByOrgId: { preferredLlm: orgPreferredLlm },
  } = useUserSafe();

  const connectedApps = useAppConnectionStatus();

  const sortedInstantConnectedApps = useMemo(() => {
    const instantConnectedApps = connectedApps.reduce(
      (acc: ConnectedApps[], curr) => {
        if (
          curr.showInAppStore &&
          curr.isInstant &&
          curr.statusCode !== AppState.ErrorReconnect
        ) {
          acc.push({
            appName: curr.appName,
            displayName: curr.displayName,
            id: curr.id,
            connectionName: curr.connectionName,
            workspaceName: curr.workspaceName,
          });
        } else if (
          curr.showInAppStore &&
          !curr.isInstant &&
          curr.statusCode === AppState.IndexingComplete
        ) {
          acc.push({
            appName: curr.appName,
            displayName: curr.displayName,
            id: curr.id,
            workspaceName: curr.workspaceName,
          });
        }

        return acc;
      },
      []
    );

    return instantConnectedApps.sort((appA, appB) => {
      return appA.appName.localeCompare(appB.appName);
    });
  }, [connectedApps]);

  const [searchQuery, setSearchQuery] = useState('');
  const [mentions, setMentions] = useState<string | undefined>();
  const [searchFilters, setSearchFilters] = useState<ConnectedApps[]>(
    sortedInstantConnectedApps
  );

  const [dynamicPlaceholder, setDynamicPlaceholder] = useState('');
  const [isBaseLLM, setIsBaseLLM] = useState(false);
  const [selectedLlm, setSelectedLlm] = useState<SupportedLlm | null>(
    userPreferredLlm ?? orgPreferredLlm ?? SupportedLlm.OPENAI_GPT4_TURBO
  );

  const previousSortedAppsRef = useRef(sortedInstantConnectedApps);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isLoaded = useRef(false);

  const states = useMemo(
    () => ({
      searchQuery,
      mentions,
      searchFilters,
      dynamicPlaceholder,
      isBaseLLM,
      selectedLlm,
      sortedInstantConnectedApps,
      setSearchQuery,
      setMentions,
      setSearchFilters,
      setDynamicPlaceholder,
      setIsBaseLLM,
      setSelectedLlm,
      previousSortedAppsRef,
      inputRef,
      isLoaded,
    }),
    [
      searchQuery,
      mentions,
      searchFilters,
      dynamicPlaceholder,
      isBaseLLM,
      selectedLlm,
      sortedInstantConnectedApps,
    ]
  );

  return <context.Provider value={states}>{children}</context.Provider>;
};
